'use client';

import { c as _c } from "react/compiler-runtime";
import { InfoCircle, Minus, Plus } from '@byway/icons';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useInteractiveLoader } from '~/components/PartnerLoader/PartnerLoader';
import { useTripOverviewSummary } from '~/components/TripOverview/hooks/useTripOverviewSummary';
import { useIsTripUpdating } from '~/hooks/useIsTripUpdating';
import { api } from '~/libs/api';
import stepperStyles from '../../NumericStepper/src/NumericStepper.module.css';
import styles from './AccommodationCardNightStepper.module.css';
type AccommodationCardNightStepperProps = {
  nights: number;
  tripId: string;
  stayItemId: number;
};
const DEBOUNCE_MS = 3000;
export const AccommodationCardNightStepper = t0 => {
  const $ = _c(76);
  const {
    nights: initialNights,
    tripId,
    stayItemId
  } = t0;
  const router = useRouter();
  const utils = api.useUtils();
  const t = useTranslations("journey");
  const tFeedback = useTranslations("content.feedback");
  const [nights, setNights] = useState(initialNights);
  const [isCommitting, setIsCommitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const {
    isDateUnset,
    isReadOnly,
    isLiveChecking
  } = useTripOverviewSummary();
  const {
    startLoader,
    completeProgressBar
  } = useInteractiveLoader();
  const isTripUpdating = useIsTripUpdating();
  const isLocked = isDateUnset || isReadOnly || isLiveChecking || isTripUpdating && !isCommitting;
  let t1;
  if ($[0] !== initialNights || $[1] !== router || $[2] !== tFeedback || $[3] !== tripId || $[4] !== utils) {
    t1 = (result, variables) => {
      if (result?.success) {
        setErrorMessage(null);
        router.refresh();
        utils.trip.getOverview.invalidate({
          tripId
        });
        utils.trip.getPrice.invalidate({
          tripId
        });
      } else {
        setNights(initialNights);
        setErrorMessage(tFeedback("nights_update_unavailable", {
          nights: variables.newDurationNights
        }));
      }
    };
    $[0] = initialNights;
    $[1] = router;
    $[2] = tFeedback;
    $[3] = tripId;
    $[4] = utils;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== initialNights || $[7] !== tFeedback) {
    t2 = () => {
      setNights(initialNights);
      setErrorMessage(tFeedback("error_generic"));
    };
    $[6] = initialNights;
    $[7] = tFeedback;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== completeProgressBar) {
    t3 = () => {
      setIsCommitting(false);
      completeProgressBar();
    };
    $[9] = completeProgressBar;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t4 = {
      onSuccess: t1,
      onError: t2,
      onSettled: t3
    };
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const {
    mutate,
    isPending
  } = api.trip.changeDuration.useMutation(t4);
  let t5;
  if ($[15] !== mutate || $[16] !== stayItemId || $[17] !== tripId) {
    t5 = newNights => {
      mutate({
        journeyId: tripId,
        stayItemId,
        newDurationNights: newNights
      });
    };
    $[15] = mutate;
    $[16] = stayItemId;
    $[17] = tripId;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const debouncedMutate = useDebouncedCallback(t5, DEBOUNCE_MS);
  let t6;
  let t7;
  if ($[19] !== debouncedMutate) {
    t6 = () => () => {
      debouncedMutate.flush();
    };
    t7 = [debouncedMutate];
    $[19] = debouncedMutate;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  useEffect(t6, t7);
  let t8;
  if ($[22] !== debouncedMutate || $[23] !== startLoader) {
    t8 = next => {
      setErrorMessage(null);
      setNights(next);
      setIsCommitting(true);
      startLoader({
        showProgressBar: false
      });
      debouncedMutate(next);
    };
    $[22] = debouncedMutate;
    $[23] = startLoader;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const handleStep = t8;
  const t9 = errorMessage && styles.wrapperError;
  let t10;
  if ($[25] !== t9) {
    t10 = clsx(styles.wrapper, t9);
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== nights || $[28] !== t) {
    t11 = t("itinerary_night_label", {
      count: nights
    });
    $[27] = nights;
    $[28] = t;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11) {
    t12 = <span className={styles.nightsLabel}>{t11}</span>;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = clsx(stepperStyles.button, styles.stepButton);
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  const t14 = isPending || isLocked || nights <= 1;
  let t15;
  if ($[33] !== handleStep || $[34] !== nights) {
    t15 = () => handleStep(Math.max(1, nights - 1));
    $[33] = handleStep;
    $[34] = nights;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t) {
    t16 = t("itinerary_decrease_nights");
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Minus decorative={true} strokeWidth={2} />;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t14 || $[40] !== t15 || $[41] !== t16) {
    t18 = <button type="button" className={t13} data-ua-click="adjust-length-stay" disabled={t14} onClick={t15} aria-label={t16}>{t17}</button>;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== nights || $[44] !== t) {
    t19 = t("nights_count", {
      count: nights
    });
    $[43] = nights;
    $[44] = t;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== nights || $[47] !== t19) {
    t20 = <output aria-live="polite" aria-label={t19} className={stepperStyles.input}>{nights}</output>;
    $[46] = nights;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = clsx(stepperStyles.button, styles.stepButton);
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  const t22 = isPending || isLocked;
  let t23;
  if ($[50] !== handleStep || $[51] !== nights) {
    t23 = () => handleStep(nights + 1);
    $[50] = handleStep;
    $[51] = nights;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t) {
    t24 = t("itinerary_increase_nights");
    $[53] = t;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Plus decorative={true} strokeWidth={2} />;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t22 || $[57] !== t23 || $[58] !== t24) {
    t26 = <button type="button" className={t21} data-ua-click="adjust-length-stay" disabled={t22} onClick={t23} aria-label={t24}>{t25}</button>;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t18 || $[61] !== t20 || $[62] !== t26) {
    t27 = <div className={stepperStyles.wrapper}>{t18}{t20}{t26}</div>;
    $[60] = t18;
    $[61] = t20;
    $[62] = t26;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== t12 || $[65] !== t27) {
    t28 = <div className={styles.row}>{t12}{t27}</div>;
    $[64] = t12;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== errorMessage) {
    t29 = errorMessage && <div className={styles.errorRow}><InfoCircle decorative={true} size="small" /><p className={styles.errorText}>{errorMessage}</p></div>;
    $[67] = errorMessage;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== isCommitting) {
    t30 = isCommitting && <div className={styles.loadingBar} aria-hidden={true} />;
    $[69] = isCommitting;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t10 || $[72] !== t28 || $[73] !== t29 || $[74] !== t30) {
    t31 = <div className={t10}>{t28}{t29}{t30}</div>;
    $[71] = t10;
    $[72] = t28;
    $[73] = t29;
    $[74] = t30;
    $[75] = t31;
  } else {
    t31 = $[75];
  }
  return t31;
};